import { Seo } from '../components/academy/Seo';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, CheckCircle, Eye } from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <Seo siteName="CarLens" title="About CarLens | Independent Vehicle Inspectors" description="Learn how CarLens delivers independent, mobile pre-purchase vehicle inspections across Northeast Florida. No dealer affiliation — just the truth before you buy." path="/about" />
      {/* Header */}
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png" 
              alt="CarLens" 
              className="h-16 w-auto max-w-[220px] sm:h-24 md:h-36 md:max-w-none cursor-pointer object-contain"
              onClick={() => navigate('/')}
            />
          </div>
          <nav className="flex items-center gap-6">
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-16">
        <div className="text-center mb-12">
          <div className="inline-block mb-6">
            <div className="px-4 py-2 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-sm font-medium">
              About Us
            </div>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>
            About CarLens
          </h1>
        </div>

        {/* Main Content */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-3xl border border-zinc-800 p-12">
          <div className="space-y-8 text-lg leading-relaxed">
            <p className="text-zinc-300">
              CarLens was built to empower everyday car buyers with the truth behind every vehicle.
            </p>
            
            <p className="text-zinc-300">
              In a world where dealership listings and online ads often hide the details that matter most, CarLens gives consumers the clarity they deserve — <span className="text-orange-400 font-semibold">trusted, independent inspection reports</span> that reveal the real condition of a car before purchase.
            </p>
            
            <p className="text-zinc-300">
              Our certified inspection partners deliver photo-backed, technician-grade evaluations covering exterior, interior, and mechanical systems — all presented in an easy-to-read, visually verified CarLens report. Each inspection tells the full story: what's great, what's concerning, and what's next.
            </p>
            
            <p className="text-zinc-300">
              CarLens isn't another dealership tool — it's a <span className="text-orange-400 font-semibold">consumer protection platform</span>. Whether you're buying from a lot, a private seller, or online, CarLens helps you make confident, informed decisions before you commit.
            </p>

            <p className="text-zinc-300 text-xl font-medium italic">
              Because when you see the truth, you drive smarter.
            </p>

            <div className="pt-8 border-t border-zinc-800">
              <p className="text-2xl font-bold text-white text-center" style={{ fontFamily: 'Space Grotesk' }}>
                CarLens — <span className="text-orange-400">Trusted. Transparent. True Condition.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 rounded-2xl border border-zinc-800 text-center">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
              Trusted
            </h3>
            <p className="text-zinc-400">
              Certified inspectors with automotive expertise you can rely on
            </p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 rounded-2xl border border-zinc-800 text-center">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
              <Eye className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
              Transparent
            </h3>
            <p className="text-zinc-400">
              Clear, detailed reports with verified photos and findings
            </p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 rounded-2xl border border-zinc-800 text-center">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
              True Condition
            </h3>
            <p className="text-zinc-400">
              Honest assessment of every vehicle's real condition
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl border border-orange-500/30 p-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Ready to See the Truth?
          </h2>
          <p className="text-zinc-300 text-lg mb-8">
            Book your inspection today and buy with confidence
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg"
            onClick={() => navigate('/book')}
            data-testid="book-inspection-about-btn"
          >
            Book Inspection
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-zinc-500">
          <p>© 2025 CarLens Inspections. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
